/* eslint-disable complexity */
import React, { forwardRef, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { getForwardedRef } from '@adac-mp/shared';
import { useViewport } from '../../hooks/useViewport';
import { HelperTextWithIcon, DropdownArrowDown } from '../../index';

type DropdownItemType = {
	label: string;
	value: string;
	id?: string;
};

interface IDropdownProps {
	['data-cy']?: string;
	arrowSize?: string;
	arrowStyle?: React.CSSProperties;
	arrowVariant?: 'regular' | 'bold';
	className?: string;
	contentClassName?: string;
	contentStyle?: React.CSSProperties;
	defaultValue?: string;
	disabled?: boolean;
	dropdownColor?: DropdownColor;
	hasError?: boolean;
	hasPortal?: boolean;
	hasWarning?: boolean;
	helperText?: string;
	icon?: React.ReactNode;
	iconStyle?: React.CSSProperties;
	id?: string;
	items: DropdownItemType[];
	placeholder?: string;
	showLabel?: boolean;
	size?: DropdownSize;
	style?: React.CSSProperties;
	value?: string;
	onBlur?: () => void;
	onFocus?: () => void;
	setValue?: (value: string, id?: string) => void;
}

enum EDropdownSize {
	lg = 'text-xl px-4 pt-4 pb-1.5',
	md = 'text-lg px-4 pt-3.5 pb-1 md:text-xl md:pt-4 md:pb-1.5',
	sm = 'text-base px-4 pt-3 pb-1',
	xs = 'text-sm py-1 px-1.5 md:text-xs',
}

enum EDropdownDefault {
	lg = 'text-xl px-4 py-3',
	md = 'text-lg px-4 py-2 md:text-xl md:py-3',
	sm = 'text-base px-4 py-1.5',
	xs = 'text-lg px-4 py-[13.4px]', //size used for lang dropdown only for now
}

enum EDropdownColor {
	default = 'bg-white',
	black = 'bg-[#191919] hover:bg-[#FFFFFF29]',
}

enum EDropdownTextColor {
	default = 'text-gray-100',
	black = 'text-gray-darkmodeText',
}

type DropdownColor = keyof typeof EDropdownColor;

type DropdownSize = keyof typeof EDropdownSize;

type Ref = HTMLElement[] | HTMLElement;

// eslint-disable-next-line prefer-arrow-callback
export const Dropdown = forwardRef<Ref, IDropdownProps>((props, ref) => {
	const {
		'data-cy': dataCy = 'Dropdown',
		arrowSize = '24',
		arrowStyle = {},
		arrowVariant = 'regular',
		className = '',
		contentClassName = '',
		contentStyle = {},
		defaultValue,
		disabled = false,
		dropdownColor = 'default',
		hasError = false,
		hasPortal = true,
		hasWarning = false,
		helperText = '',
		icon,
		iconStyle = {},
		id = 'dropdown',
		items,
		placeholder,
		showLabel,
		size = 'md',
		style = undefined,
		value = '',
		setValue = () => {},
		onFocus = () => {},
		onBlur = () => {},
	}: IDropdownProps = props;

	const { viewport } = useViewport();
	const isDesktop = viewport === 'desktop';
	const [isOpened, setIsOpened] = useState(false);

	const renderDropdownMenuContent = () => (
		<DropdownMenu.Content
			className={`bg-white rounded border border-gray-600 shadow z-[3000] mt-1 ${
				isDesktop ? 'max-h-[400px]' : 'max-h-[200px]'
			} overflow-scroll absolute ${contentClassName}`}
			style={contentStyle}
			align="start"
			onKeyDown={(e) => {
				if (e.key === 'Escape') {
					e.stopPropagation();
					setIsOpened(false);
				}
			}}
			onCloseAutoFocus={(e) => e.preventDefault()}>
			{items.map((item, index) => (
				<DropdownMenu.Item
					key={item.value}
					data-cy={`${dataCy} item: ${index}`}
					className={`cursor-pointer ${EDropdownDefault[size]} highlighted:outline-none hover:bg-black/[.04] text-gray-200 hover:text-gray-100`}
					onSelect={() => setValue(item.value, item.id)}>
					{item.label}
				</DropdownMenu.Item>
			))}
		</DropdownMenu.Content>
	);

	const getDropdownIconColor = () => {
		if (disabled) return '#A3A3A3';
		if (dropdownColor === 'black') return '#CCCCCC';
		return;
	};

	return (
		<>
			<DropdownMenu.Root
				open={isOpened}
				onOpenChange={() => {
					setIsOpened(!isOpened);
				}}>
				<DropdownMenu.Trigger
					ref={(el) => getForwardedRef(el, ref)}
					id={id}
					data-cy={dataCy}
					disabled={disabled}
					defaultValue={defaultValue}
					className={`${
						disabled ? 'cursor-default' : 'cursor-pointer'
					} mb-1.5 border-0 border-solid flex items-center appearance-none justify-between relative rounded font-bold ${
						disabled || dropdownColor === 'black' ? '' : 'hover:shadow-innerHover'
					} focus:shadow-innerHover focus-visible:outline-none ${
						value !== '' && placeholder ? EDropdownSize[size] : EDropdownDefault[size]
					} ${hasError && 'shadow-innerError'} ${hasWarning && 'shadow-innerWarning'} ${
						EDropdownColor[dropdownColor]
					} ${className}`}
					style={style}
					onFocus={onFocus}
					onBlur={onBlur}>
					<>
						{icon && (
							<span
								className="absolute inline-flex pointer-events-none h-fit top-1/2 -translate-y-1/2"
								style={iconStyle}>
								{icon}
							</span>
						)}
						{!(size === 'xs' && value !== '') && (
							<label
								className={`${
									disabled ? 'cursor-default text-gray-400' : 'cursor-pointer text-gray-300'
								} block overflow-hidden whitespace-nowrap overflow-ellipsis max-w-full ${
									value !== '' ? 'absolute text-xs top-1.5 -translate-y-0.5' : ''
								}`}>
								{placeholder}
							</label>
						)}

						{value !== '' && (
							<label
								data-cy={`label-${dataCy}`}
								className={`${
									disabled
										? 'cursor-default text-gray-400'
										: `cursor-pointer ${EDropdownTextColor[dropdownColor]}`
								}`}>
								{showLabel
									? items.find((item) => item.value === value)?.value
									: items.find((item) => item.value === value)?.label}
							</label>
						)}
						<span
							className={`inline-flex transition-all duration-150 ${isOpened && 'rotate-180'} ${
								value !== '' && size !== 'xs' && '-translate-y-[6px]'
							}`}
							style={arrowStyle}>
							<DropdownArrowDown
								bold={arrowVariant === 'bold'}
								size={arrowSize}
								fill={getDropdownIconColor()}
							/>
						</span>
					</>
				</DropdownMenu.Trigger>
				{hasPortal ? (
					<DropdownMenu.Portal>{renderDropdownMenuContent()}</DropdownMenu.Portal>
				) : (
					renderDropdownMenuContent()
				)}
			</DropdownMenu.Root>
			<HelperTextWithIcon helperText={helperText} hasError={hasError} hasWarning={hasWarning} />
		</>
	);
});

Dropdown.displayName = 'Dropdown';
